import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { toast } from 'react-toastify';
import { Send, ArrowLeft, CheckCircle } from 'lucide-react';
import { useIframeCommunication } from '../../hooks/useIframeCommunication';
import { useConfig } from '../../contexts/ConfigContext';
import Footer from '../../components/Footer';
import './MatchingApplicationPage.css';

const MatchingApplicationPage = () => {
    useIframeCommunication();
    const { config } = useConfig();
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        preferred_contact: '',
        request_description: '',
        price_min: '',
        price_max: '',
        is_student: false,
        privacy_consent: false
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.first_name.trim()) {
            toast.error('Введите имя');
            return;
        }
        if (!formData.last_name.trim()) {
            toast.error('Введите фамилию');
            return;
        }
        if (!formData.phone.trim() || formData.phone.trim().length < 10) {
            toast.error('Введите корректный телефон');
            return;
        }
        if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
            toast.error('Введите корректный email');
            return;
        }
        if (config?.code !== 'mag' && !formData.preferred_contact.trim()) {
            toast.error('Укажите удобный способ связи');
            return;
        }
        if (!formData.privacy_consent) {
            toast.error('Необходимо согласие на обработку персональных данных');
            return;
        }

        const priceMin = formData.price_min !== '' ? Number(String(formData.price_min).replace(',', '.')) : null;
        const priceMax = formData.price_max !== '' ? Number(String(formData.price_max).replace(',', '.')) : null;
        if (priceMin != null && priceMax != null && priceMin > priceMax) {
            toast.error('Минимальная стоимость не может быть больше максимальной');
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${API_BASE_URL}/therapist-matching/applications`, {
                first_name: formData.first_name.trim(),
                last_name: formData.last_name.trim(),
                phone: formData.phone.trim(),
                email: formData.email.trim(),
                ...(config?.code !== 'mag' ? {preferred_contact: formData.preferred_contact.trim()} : {}),
                request_description: formData.request_description.trim() || null,
                price_min: priceMin,
                price_max: priceMax,
                is_student: formData.is_student,
                privacy_consent: true
            });
            setSubmitted(true);
            toast.success('Заявка отправлена');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Ошибка отправки заявки');
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="matching-application-page">
                <div className="matching-application-container matching-success">
                    <CheckCircle size={56} className="matching-success-icon" />
                    <h1>Заявка отправлена</h1>
                    <p>Мы свяжемся с вами после подбора специалиста.</p>
                    <Link to="/public/therapists" className="btn btn-primary">
                        К списку терапевтов
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="matching-application-page">
            <div className="matching-application-container">
                <Link to="/public/therapists" className="back-link">
                    <ArrowLeft size={16} />
                    Назад к магрегатору
                </Link>

                <div className="form-header">
                    <h1>Заявка на подбор специалиста</h1>
                    <p>Опишите запрос — мы поможем подобрать терапевта</p>
                </div>

                <form onSubmit={handleSubmit} className="matching-form">
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="first_name">
                                Имя <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                id="first_name"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleChange}
                                required
                                placeholder="Иван"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="last_name">
                                Фамилия <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                id="last_name"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleChange}
                                required
                                placeholder="Иванов"
                            />
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="phone">
                            Телефон <span className="required">*</span>
                        </label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            placeholder="+7 (999) 123-45-67"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">
                            Почта <span className="required">*</span>
                        </label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="ivan@example.com"
                        />
                    </div>

                    {config?.code !== 'mag' && <div className="form-group">
                        <label htmlFor="preferred_contact">
                            Удобный способ связи <span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="preferred_contact"
                            name="preferred_contact"
                            value={formData.preferred_contact}
                            onChange={handleChange}
                            required
                            placeholder="Telegram, WhatsApp, звонок..."
                        />
                    </div>}

                    <div className="form-group">
                        <label htmlFor="request_description">Краткое описание вашего запроса</label>
                        <textarea
                            id="request_description"
                            name="request_description"
                            value={formData.request_description}
                            onChange={handleChange}
                            rows={4}
                            placeholder="С чем хотите поработать..."
                        />
                    </div>

                    <div className="form-group">
                        <label>Диапазон стоимости (₽)</label>
                        <div className="form-row">
                            <input
                                type="number"
                                name="price_min"
                                min="0"
                                step="100"
                                value={formData.price_min}
                                onChange={handleChange}
                                placeholder="от"
                            />
                            <input
                                type="number"
                                name="price_max"
                                min="0"
                                step="100"
                                value={formData.price_max}
                                onChange={handleChange}
                                placeholder="до"
                            />
                        </div>
                    </div>

                    <div className="form-check-group">
                        <div className="form-check">
                            <input
                                type="checkbox"
                                id="is_student"
                                checked={formData.is_student}
                                onChange={(e) => setFormData((prev) => ({ ...prev, is_student: e.target.checked }))}
                            />
                            <label htmlFor="is_student">
                                Я студент — мне нужны часы терапии в зачёт
                            </label>
                        </div>

                        <div className="form-check">
                            <input
                                type="checkbox"
                                id="privacy_consent"
                                checked={formData.privacy_consent}
                                onChange={(e) => setFormData((prev) => ({ ...prev, privacy_consent: e.target.checked }))}
                                required
                            />
                            <label htmlFor="privacy_consent">
                                Даю{' '}
                                <a
                                    href={config?.privacyPolicyUrlConsent || 'https://mag-gestalt.ru/mag/consent-personal-data/'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="consent-link"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    согласие
                                </a>
                                {' на обработку персональных данных в соответствии с '}
                                <a
                                    href={config?.privacyPolicyUrl || 'https://mag-gestalt.ru/mag/privacy-policy/'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="consent-link"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    политикой обработки персональных данных
                                </a>
                                <span className="required">*</span>
                            </label>
                        </div>
                    </div>

                    <button type="submit" className="btn btn-primary matching-submit" disabled={loading}>
                        <Send size={18} />
                        {loading ? 'Отправка…' : 'Отправить заявку'}
                    </button>
                </form>
            </div>
            <Footer />
        </div>
    );
};

export default MatchingApplicationPage;
